/* Independent carousel for the three original opening slides. */
class DGHeroCarousel {
  constructor(root) {
    this.root = root;
    this.slides = Array.from(root.querySelectorAll('.dg-hero-slide'));
    this.dots = Array.from(root.querySelectorAll('[data-dg-dot]'));
    this.count = root.querySelector('[data-dg-count]');
    this.pauseButton = root.querySelector('[data-dg-pause]');
    this.index = 0;
    this.timer = null;
    this.focused = false;
    this.keyboardMode = false;
    this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.media.matches;
    this.slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${i + 1} of ${this.slides.length}`);
    });
    root.querySelector('[data-dg-prev]')?.addEventListener('click', () => this.move(-1));
    root.querySelector('[data-dg-next]')?.addEventListener('click', () => this.move(1));
    this.pauseButton?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.updatePause();
      this.schedule();
    });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.index = index;
        this.show();
        this.schedule();
      });
    });
    this.onInputKey = event => {
      if (['Tab', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key)) this.keyboardMode = true;
    };
    this.onPointerInput = () => {
      this.keyboardMode = false;
      this.focused = false;
      this.schedule();
    };
    document.addEventListener('keydown', this.onInputKey, true);
    document.addEventListener('pointerdown', this.onPointerInput, {passive: true});
    document.addEventListener('touchstart', this.onPointerInput, {passive: true});
    root.addEventListener('focusin', () => {
      this.focused = this.keyboardMode;
      this.schedule();
    });
    root.addEventListener('focusout', () => queueMicrotask(() => {
      this.focused = this.keyboardMode && root.contains(document.activeElement);
      this.schedule();
    }));
    root.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    root.addEventListener('touchstart', event => { this.touchX = event.touches[0].clientX; }, {passive: true});
    root.addEventListener('touchend', event => {
      if (typeof this.touchX !== 'number') return;
      const delta = event.changedTouches[0].clientX - this.touchX;
      this.touchX = null;
      if (Math.abs(delta) > 50) this.move(delta < 0 ? 1 : -1);
    }, {passive: true});
    this.onVisibility = () => {
      this.focused = this.keyboardMode && root.contains(document.activeElement);
      this.schedule();
    };
    window.addEventListener('pageshow', this.onVisibility);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onMotionChange = event => {
      this.paused = event.matches;
      this.updatePause();
      this.schedule();
    };
    this.media.addEventListener('change', this.onMotionChange);
    this.show();
    this.updatePause();
    this.schedule();
  }
  show() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.hidden = false; // Keep slides layered for the cross-fade.
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });
    if (this.count) this.count.textContent = `${this.index + 1} / ${this.slides.length}`;
  }
  updatePause() {
    if (!this.pauseButton) return;
    this.pauseButton.textContent = this.paused ? 'Play' : 'Pause';
    this.pauseButton.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
  }
  move(direction) {
    this.index = (this.index + direction + this.slides.length) % this.slides.length;
    this.show();
    this.schedule();
  }
  schedule() {
    clearTimeout(this.timer);
    this.timer = null;
    const running = !this.paused && !this.focused && !document.hidden && this.slides.length > 1;
    this.root.setAttribute('data-dg-autoplay', String(running));
    if (!running) return;
    this.timer = setTimeout(() => {
      if (!this.root.isConnected) { this.destroy(); return; }
      this.move(1);
    }, 3000);
  }
  destroy() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pageshow', this.onVisibility);
    document.removeEventListener('keydown', this.onInputKey, true);
    document.removeEventListener('pointerdown', this.onPointerInput);
    document.removeEventListener('touchstart', this.onPointerInput);
    this.media.removeEventListener('change', this.onMotionChange);
  }
}
function initDGHeroes() {
  document.querySelectorAll('[data-dg-hero]').forEach(root => {
    if (!root.dgCarousel) root.dgCarousel = new DGHeroCarousel(root);
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDGHeroes);
else initDGHeroes();
document.addEventListener('shopify:section:load', initDGHeroes);
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-dg-hero]').forEach(root => root.dgCarousel?.destroy());
});
/* Open only the actual review form provided by Judge.me, respecting its collection settings. */
document.addEventListener('click', event => {
  const button = event.target.closest('[data-dg-write-review]');
  if (!button || button.disabled) return;
  const section = button.closest('.dg-review-section');
  const status = section.querySelector('[data-dg-review-status]');
  let attempts = 0;
  button.disabled = true;
  status.hidden = false;
  status.textContent = 'Opening the review form…';
  const tryOpen = () => {
    if (!section.isConnected) return;
    const write = section.querySelector('.jdgm-write-rev-link, [data-testid="write-review-button"]');
    if (write && write.getClientRects().length && !write.disabled && write.getAttribute('aria-disabled') !== 'true') {
      write.click();
      section.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
      status.hidden = true;
      button.disabled = false;
      return;
    }
    if (++attempts < 20) { setTimeout(tryOpen, 250); return; }
    status.textContent = 'Review submissions are currently unavailable. Please try again later.';
    button.disabled = false;
  };
  tryOpen();
});
